import type {
  IMapLayoutCatalog,
  IMapLayoutElement,
  IMapLayoutElementGeometry,
  IMapLayoutElementSpots,
} from '@interfaces/map';

// UE owns the catalog of pre-built room elements and the arrangement — it auto-chains
// rooms by their exits. The web is only a picker: it chooses and orders element ids and
// never authors geometry, so every geometry field below is carried for display/debug
// only and is deliberately not sent back in SPAWN_LAYOUT.
//
// At runtime the catalog comes from the build: the MytaMapEditor GameCast metadata
// provider emits it under plugins.mapEditor, spatial-manager projects it onto the build
// record, and the hub reads pixelStreamingClient.clientParams.layoutCatalogs, keyed by the
// ueIdentifier of the host map. A build that shipped none has no map editor at all — see
// resolveCatalogElements and useCanUseMapEditor.
// What a module is: the first of the three questions the picker asks. Kept a plain string
// rather than a union because a newer build may ship a family this hub has never heard of,
// and the picker groups on whatever it finds rather than dropping what it cannot name.
export const MODULE_FAMILY_ROOM = 'room';
export const MODULE_FAMILY_CORRIDOR = 'corridor';

// The walker's left/right — matching the art's _Left / _Right names and the sign of the
// module's yaw step. NOT the legacy `type`'s left/right, which were inverted.
export const MODULE_DIRECTION_STRAIGHT = 'straight';

// 'end' is a cap, not a link. UE drops every room requested after one ('chain-closed'), so caps
// never appear in the picker and are appended to a published chain as its last room instead.
//
// 'start' is the lobby the chain opens in — the mirror image: a module with an exit and nothing
// before it. UE refuses one anywhere but at index 0 ('start-not-first'), so it is kept out of the
// picker for the same reason a cap is and chosen through its own control instead.
export type MapEditorElementRole = 'module' | 'end' | 'start';

// The roles that are a position in the chain rather than a link in it. Both are excluded from the
// picker, and both are attached to a published chain by this hub rather than by the author.
//
// Exported because the report parser has to answer the same question — an anchor row coming back is
// never one of the author's rooms — and spelling the pair out a second time there is how a third
// anchor role would get silently folded into the author's chain.
export const CHAIN_ANCHOR_ROLES: MapEditorElementRole[] = ['end', 'start'];

/**
 * Whether a role is an anchor rather than a link in the chain.
 *
 * @param raw - A role as it arrived, from the catalog or off the wire.
 * @returns True for the roles this hub attaches itself and never offers in the picker.
 */
export const isChainAnchorRole = (raw: unknown): boolean =>
  CHAIN_ANCHOR_ROLES.some((role) => role === raw);

export interface MapEditorCatalogElement {
  id: string;
  displayName: string;
  category: string;
  // Authored prose, shown in the wall-layout step. Empty when the kit supplies none, which is the
  // normal case — the spot counts stand in for it.
  description: string;
  thumbnailUrl: string;
  // The full-size render, for the two surfaces that fill a frame with it — the picker's chosen
  // segment and the segment inspector. Empty on every build that did not run the render, which is
  // most of them, and those fall back to the tile.
  previewUrl: string;
  // The pre-split fused type, carried for display and debugging only. Never sent back: UE reads
  // the family/direction pair now, and this field's left/right were backwards.
  type: string;
  family: string;
  direction: string;
  size: string;
  role: MapEditorElementRole;
  // Empty means the element is catalogued but has no room level built yet — UE spawns
  // nothing for it. Surfaced in the tree so an author is not left wondering why a room
  // they picked never appeared.
  geometryLevel: string;
  geometryClass: string;
  overrideExit: boolean;
  exitOffset: number[];
  exitYawDeg: number;
  // The build's own verdict on this element's geometry. Optional because the field post-dates the
  // rest of the catalog, so every reader below tolerates it missing rather than treating absence
  // as a negative answer.
  geometry?: IMapLayoutElementGeometry;
  // Absent means the build could not read the level, which is a different answer from a level
  // that really holds no spots. Both exist in the shipped kit.
  spots?: IMapLayoutElementSpots;
  // Semantic surfaces this element can be re-finished on, lower-cased and deduped. Empty for a
  // module whose surfaces nobody has classified yet, and for a build older than the field — the
  // finish menu offers nothing for either, which is the same correct answer.
  finishSlots: string[];
  // How big this module is on the plan, or null when the build measured nothing. Null is what a
  // drawing has to be able to draw *around*: a glyph is not the point of the picker card, and a
  // module the build could not measure is still pickable.
  footprint: MapEditorFootprint | null;
}

// A module's size on the plan, in centimetres: `lengthCm` along the walk, `widthCm` across it.
//
// Named after what they mean rather than after the axes they came from. The catalog's four numbers
// are a box in the module's own frame, where +X is always the direction the walk enters along — so
// the box is turned into these two before anything draws with it, and no drawing has to know which
// way a corridor happens to sweep.
export interface MapEditorFootprint {
  lengthCm: number;
  widthCm: number;
}

// 'fallback' means the map's build shipped no catalog. It carries no elements: useCanUseMapEditor
// gates every map-editor surface on 'build', so on a map without a kit there is no picker to fill,
// no inspector to look up ids in, and nothing to publish. A stand-in list existed here once and
// could not be reached from anywhere — worse, its ids were the test map's, so anything that did
// reach it would have offered rooms the map cannot build.
export type CatalogSource = 'build' | 'fallback';

const NO_CATALOG = {
  elements: [] as MapEditorCatalogElement[],
  source: 'fallback' as const,
  defaultEndElement: '',
  defaultStartElement: '',
};

// The pre-split `type` read as the pair, mapped by the turn each string actually produced
// rather than by the word in it: `corridor-left` swept toward +Y, which is the walker's
// *right*, and the catalog worked around that by labelling the art's _Right modules
// `corridor-left`. Mirrors MytaMapEditor::ParseLegacyType exactly, so a catalog shipped
// before the split builds the same world it always did.
const LEGACY_TYPE_FACETS: Record<
  string,
  { family: string; direction: string }
> = {
  'corridor-left': { family: MODULE_FAMILY_CORRIDOR, direction: 'right' },
  'corridor-right': { family: MODULE_FAMILY_CORRIDOR, direction: 'left' },
  gallery: {
    family: MODULE_FAMILY_ROOM,
    direction: MODULE_DIRECTION_STRAIGHT,
  },
};

// UE resolves an unnamed family to Room and an unnamed direction to Straight, so the same
// defaults are applied here: the picker's promise is that what it offers is what UE builds,
// and leaving these blank would put a real, spawnable element in a nameless bucket.
//
// Keyed on either field being *present*, not on it carrying a value, because that is what
// ParseCatalog does: an authored `"family": ""` is an answer — the default — and it stops UE
// looking at the legacy `type` entirely. Reading it as a gap instead sent the picker down the
// legacy mapping and offered a turn the element does not make.
const resolveFacets = (
  element: IMapLayoutElement,
): { family: string; direction: string } => {
  if (element.family !== undefined || element.direction !== undefined) {
    return {
      family: (element.family ?? '').trim() || MODULE_FAMILY_ROOM,
      direction: (element.direction ?? '').trim() || MODULE_DIRECTION_STRAIGHT,
    };
  }

  const legacy = LEGACY_TYPE_FACETS[(element.type ?? '').trim().toLowerCase()];

  return (
    legacy ?? {
      family: MODULE_FAMILY_ROOM,
      direction: MODULE_DIRECTION_STRAIGHT,
    }
  );
};

/**
 * Reads an element's role.
 *
 * @param raw - The catalog's `role`, or whatever was found in its place.
 * @returns The role, defaulting to 'module' for anything this hub cannot name.
 */
const resolveRole = (raw: string | undefined): MapEditorElementRole =>
  CHAIN_ANCHOR_ROLES.find((role) => role === raw) ?? 'module';

const normalizeElement = (
  element: IMapLayoutElement,
): MapEditorCatalogElement => ({
  id: element.id,
  displayName: element.displayName || element.id,
  // Ungrouped elements would otherwise vanish from a category-keyed tree.
  category: element.category || 'Other',
  description: element.description ?? '',
  thumbnailUrl: element.thumbnailUrl ?? '',
  previewUrl: element.previewUrl ?? '',
  type: element.type ?? '',
  ...resolveFacets(element),
  size: element.size ?? '',
  // Anything this hub cannot name is a module: 'end' and 'start' are the roles that change
  // behaviour, and reading an unknown value as either would hide a pickable element from the
  // picker — or, worse, offer it as a lobby the chain cannot open in.
  role: resolveRole(element.role),
  geometryLevel: element.geometryLevel ?? '',
  geometryClass: element.geometryClass ?? '',
  overrideExit: element.overrideExit ?? false,
  exitOffset: element.exitOffset ?? [0, 0, 0],
  exitYawDeg: element.exitYawDeg ?? 0,
  // Passed through rather than flattened into booleans here: the shape is the build's, new
  // fields appear in it without a UE code change, and normalising early would drop them.
  geometry: element.geometry,
  spots: element.spots,
  finishSlots: normalizeFinishSlots(element.finishSlots),
  footprint: normalizeFootprint(element.footprint),
});

/**
 * Reads an element's plan footprint.
 *
 * Rejects a degenerate box rather than carrying it, because a zero span is not a small module: it is
 * what a build writes when it measured nothing, and a glyph drawn from it collapses to a line. The
 * caller then draws the no-measurement card, which says so.
 *
 * @param raw - The element's `footprint`, or whatever was found in its place.
 * @returns The size along and across the walk, or null when the four numbers are not there.
 */
const normalizeFootprint = (raw: unknown): MapEditorFootprint | null => {
  if (!Array.isArray(raw) || raw.length < 4) {
    return null;
  }

  const box = raw.slice(0, 4);
  if (box.some((value) => typeof value !== 'number' || !isFinite(value))) {
    return null;
  }

  const [minX, minY, maxX, maxY] = box as number[];
  const lengthCm = Math.abs(maxX - minX);
  const widthCm = Math.abs(maxY - minY);

  return lengthCm > 0 && widthCm > 0 ? { lengthCm, widthCm } : null;
};

/**
 * Reads an element's semantic surfaces.
 *
 * Lower-cased and deduped on the way in even though the build already lower-cases them, because the
 * value is compared and diffed against the `finishSlots` of a geometry report: those are FNames,
 * which render whichever casing reached the process name table first, and a surface that read as
 * `Ceiling` in one session and `ceiling` in the next would look like the kit had changed.
 *
 * @param raw - The element's `finishSlots`, or whatever was found in its place.
 * @returns The semantics, in the order the build listed them.
 */
const normalizeFinishSlots = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seen = new Set<string>();

  return raw.reduce<string[]>((slots, entry) => {
    const slot = typeof entry === 'string' ? entry.trim().toLowerCase() : '';
    if (!slot || seen.has(slot)) {
      return slots;
    }
    seen.add(slot);

    return [...slots, slot];
  }, []);
};

// Only an id this catalog can actually describe counts. UE emits the field only for a cap the
// build ships, but the catalog travels through spatial-manager and a DB record that may be
// older than the build, and an unresolvable id would publish a room with no geometry — a grey
// stub sealing a live world's layout.
//
// The geometry condition is the same one UE applies to its own auto-close (CloseLayoutChain:
// "the chain is left open rather than closed with a greybox stub"), and it has to be applied
// here too because the two halves append the cap on different paths: UE's runs only where a
// catalog can be read, and a packaged server reads none — so on the streamed path this hub is
// the only side that decides. Without it the web published exactly the grey box UE refuses to
// build, and the same map ended in a wall or in a placeholder depending on which side capped it.
//
// Deliberately the declared references rather than `hasBuiltGeometry`, which also fails an
// element whose level was left out of the cook. That case UE *does* build — it greyboxes it and
// the inspector marks the row — so rejecting it here would replace one divergence with another.
//
// The lobby is resolved through the same function and on the same two conditions, because it
// reaches the wire the same way: this hub sends the start row on every path rather than only on the
// streamed one, so an id it cannot describe would publish a room with no geometry to build it from.
/**
 * Resolves a catalog-declared default anchor to an id that is safe to put on the wire.
 *
 * @param id - The catalog's `defaultEndElement` / `defaultStartElement`, or whatever was in its place.
 * @param elements - The resolved catalog.
 * @param role - The role the named element has to actually carry.
 * @returns The element id, or empty when it is missing, the wrong role, or declares no geometry.
 */
const resolveDefaultElement = (
  id: string | undefined,
  elements: MapEditorCatalogElement[],
  role: MapEditorElementRole,
): string => {
  const wanted = (id ?? '').trim();
  if (!wanted) {
    return '';
  }

  const element = elements.find((candidate) => candidate.id === wanted);
  if (element?.role !== role) {
    return '';
  }

  const declaresGeometry =
    !!element.geometryLevel.trim() || !!element.geometryClass.trim();

  return declaresGeometry ? element.id : '';
};

// Defensive on purpose: this payload crosses UE → metadata.json → spatial-manager →
// DB → API before arriving, so anything malformed falls back rather than throwing
// inside a render. An entry without an id is unusable — it is what SPAWN_LAYOUT sends.
export const resolveCatalogElements = (
  catalog: IMapLayoutCatalog | undefined | null,
): {
  elements: MapEditorCatalogElement[];
  source: CatalogSource;
  defaultEndElement: string;
  defaultStartElement: string;
} => {
  const rawElements = catalog?.elements;
  if (!Array.isArray(rawElements)) {
    return NO_CATALOG;
  }

  const elements = rawElements
    .filter(
      (element): element is IMapLayoutElement =>
        !!element && typeof element.id === 'string' && element.id.length > 0,
    )
    .map(normalizeElement);

  // An empty array from the build is treated as "no catalog shipped" rather than
  // "a catalog of nothing" — the latter would leave the author with a dead panel
  // and no signal about why.
  if (!elements.length) {
    return NO_CATALOG;
  }

  return {
    elements,
    source: 'build',
    // Resolved here rather than passed through, because a cap is only useful if it can be
    // described on the wire: a packaged server reads no catalog, so an id naming an element
    // this catalog does not carry would publish a room with no geometry to resolve it from.
    defaultEndElement: resolveDefaultElement(
      catalog?.defaultEndElement,
      elements,
      'end',
    ),
    // The kit's own default lobby, for the control to pre-select. Resolved rather than passed
    // through for the same reason the cap is, and it matters more here: UE supplies no lobby of
    // its own at all, so an id this catalog cannot describe would leave the control offering a
    // choice that publishes a room with nothing to build it from.
    defaultStartElement: resolveDefaultElement(
      catalog?.defaultStartElement,
      elements,
      'start',
    ),
  };
};

export const findCatalogElement = (
  elements: MapEditorCatalogElement[],
  id: string,
): MapEditorCatalogElement | undefined =>
  elements.find((element) => element.id === id);

/**
 * Whether a placement names a room the streamed build's kit cannot describe.
 *
 * The state MV-2004 left behind: a configuration authored against a build whose kit held the room,
 * reopened on a build whose kit does not. Nothing in the editor said so — the tree fell back to the
 * raw id as a label, the payload builder sent the id on with no family, direction or size, and UE
 * turned it down — so the author was shown a refusal for a room that was never going to build,
 * rather than the reason it was not.
 *
 * The `elements.length` guard is what keeps the answer honest. A build shipping no kit resolves to
 * no elements and has no map editor at all (useCanUseMapEditor), so answering "missing" for every id
 * there would condemn a whole layout on the strength of having read nothing.
 *
 * @param elements - The resolved kit of the build being streamed.
 * @param id - The id the placement holds; empty for a placement that names none.
 * @returns True only when there is a kit to check against and this id is not in it.
 */
export const isMissingFromKit = (
  elements: MapEditorCatalogElement[],
  id: string,
): boolean => !!id && !!elements.length && !findCatalogElement(elements, id);

/**
 * Whether a chain already ends in a cap, and so must not be given another.
 *
 * The picker never offers one, but a chain restored from the branding record can still end in a cap:
 * the flat catalog list that preceded the picker had no notion of a role and listed every element,
 * so a layout saved then may carry one as a room the author picked.
 *
 * Shared by the two places that have to agree about it — the payload builder, which would otherwise
 * append a second cap UE refuses as `chain-closed`, and the Layout panel, which would otherwise show
 * two walls for the one the payload carries.
 *
 * @param roomIds - The chain, in order.
 * @param elements - The resolved catalog. An id it cannot describe is not a cap.
 * @returns True when the last room is an End element.
 */
export const chainEndsInCap = (
  roomIds: string[],
  elements: MapEditorCatalogElement[],
): boolean => {
  const lastId = roomIds[roomIds.length - 1];

  return !!lastId && findCatalogElement(elements, lastId)?.role === 'end';
};

/**
 * Whether a chain already opens on a purpose-built lobby shell, and so must not be given another.
 *
 * The mirror of chainEndsInCap, and it guards a refusal that is worse to trigger: a second start row
 * is reported as `start-not-first`, which names a fault in the sender rather than in the layout, so
 * an author would be shown a problem they cannot act on.
 *
 * Deliberately about the *shells* rather than about entrances in general, and that is the whole
 * distinction `role: "start"` now carries: any element may be an entrance, so an ordinary room at
 * the head of `rooms` is an ordinary first link and prepending an entrance in front of it is exactly
 * right. A shell there is different — it hangs its geometry behind its own exit and cannot be a link
 * at all, so it can only have got there as an entrance somebody folded into `rooms`.
 *
 * Nothing the picker offers can produce this: the shells are kept out of the module grid, and the
 * entrance is chosen through its own control and lives beside the rooms rather than in them.
 *
 * @param roomIds - The chain, in order.
 * @param elements - The resolved catalog. An id it cannot describe is not a shell.
 * @returns True when the first room is a Start element.
 */
export const chainStartsInLobby = (
  roomIds: string[],
  elements: MapEditorCatalogElement[],
): boolean => {
  const [firstId] = roomIds;

  return !!firstId && findCatalogElement(elements, firstId)?.role === 'start';
};

/**
 * Resolves an author's chosen entrance to the id this hub will actually publish.
 *
 * The single answer to "does this saved choice still name a room this build can open on", asked by
 * everything that has to agree about it: the payload builder, which cannot send a row it has no
 * geometry for; the entry republish, whose decision to publish at all turns on whether there is an
 * entrance to carry; and the draft, which would otherwise hold a choice that is never sent and so
 * never matches what comes back standing — an "unsaved" state on every entry with nothing on screen
 * that differs.
 *
 * Any element the catalog describes qualifies, on the same rule getEntranceElements offers them by:
 * opening a chain is a property of the row rather than of the element. The cap is the one exclusion,
 * for the reason given there.
 *
 * Deliberately not the `declaresGeometry` test resolveDefaultElement applies. That one guards a
 * default the kit named on the author's behalf, where a grey box at the arrival point is nobody's
 * decision; this is an entrance the author picked from a control that marks the greybox cases, so
 * refusing to send it would drop a choice they made and were warned about.
 *
 * @param id - The layout's `startElement`, or whatever was found in its place.
 * @param elements - The resolved catalog. Empty means nothing can be resolved, not that it is fine.
 * @returns The element id when this catalog can open a chain on it, empty otherwise.
 */
export const resolveStartElement = (
  id: string | undefined,
  elements: MapEditorCatalogElement[],
): string => {
  const wanted = (id ?? '').trim();
  if (!wanted) {
    return '';
  }

  const element = findCatalogElement(elements, wanted);

  return element && element.role !== 'end' ? wanted : '';
};

/**
 * The elements an author may put in a chain: everything except the two anchors.
 *
 * An End element seals the chain — UE drops every room requested after one and reports them
 * as `chain-closed` — so offering one in the picker is offering a way to silently delete the
 * rest of the layout. The cap is appended to a published chain instead, as its last room.
 *
 * A Start element is the same mistake mirrored: UE refuses one anywhere but at index 0, and
 * reports it as `start-not-first` — a refusal that names a client bug, so an author who picked it
 * from a grid would be told about a fault they cannot act on. The lobby is chosen through its own
 * control and prepended at publish time.
 *
 * @param elements - The resolved catalog.
 * @returns The pickable modules, in catalog order.
 */
export const getPickableElements = (
  elements: MapEditorCatalogElement[],
): MapEditorCatalogElement[] =>
  elements.filter((element) => !CHAIN_ANCHOR_ROLES.includes(element.role));

/**
 * The rooms this kit offers as an entrance — everything the author's entrance control may choose
 * between.
 *
 * Every element, not only the Start ones. Opening a chain is a property of the *row* rather than of
 * the element: UE takes whatever arrives as `rooms[0]` carrying `role: "start"` and does not
 * cross-check it against the catalog, and an entrance whose level authors no Spawn marker now gets
 * an arrival point derived from its footprint. So `role: "start"` is not an eligibility flag — it
 * says only "this element is nothing else", which is why the two purpose-built shells stay out of
 * the *module* grid (they hang their geometry behind their own exit and cannot sit mid-chain).
 *
 * Those shells sort first, as the choices made for this. The rest follow in catalog order.
 *
 * A cap is the one element left out, and the picker is the only thing that can leave it out: UE
 * does **not** refuse a cap at the head of a chain, it builds nonsense out of it. The row arrives
 * carrying `role: "start"`, so the runtime never zeroes its exit — it does that only for a row whose
 * own role is End — the exit ladder falls back to the spec box, and the derived arrival point is
 * pinned inside the cap's 20 cm of depth. A silent absurdity is the worst of the outcomes, which is
 * why this is a filter here rather than a refusal there. The kit says the same thing in art: every
 * level but the cap authors a `Spawn` marker.
 *
 * @param elements - The resolved catalog.
 * @returns The entrance candidates: the purpose-built shells first, then everything else in catalog
 * order.
 */
export const getEntranceElements = (
  elements: MapEditorCatalogElement[],
): MapEditorCatalogElement[] => {
  const offerable = elements.filter((element) => element.role !== 'end');

  return [
    ...offerable.filter((element) => element.role === 'start'),
    ...offerable.filter((element) => element.role !== 'start'),
  ];
};

// Whether this build can actually put art on the ground for this element.
//
// Prefers the build's own verdict over the declared path, because the two disagree in the case
// that matters: an element can name a room level the build never cooked, and the declaration
// looks fine while the spawn comes out a placeholder box.
export const hasBuiltGeometry = (element: MapEditorCatalogElement): boolean => {
  const { geometry } = element;
  if (!geometry) {
    // A build that predates the verdict field. The declared level is the only evidence there is —
    // which is what this check has always used.
    return (element.geometryLevel ?? '').trim().length > 0;
  }

  // `cooked` is conditionally absent by design (class geometry, or an unreadable cook list) and
  // absent means unknown, so only an explicit false counts against it.
  return (
    geometry.kind !== 'greybox' &&
    geometry.available &&
    geometry.cooked !== false
  );
};

// The element declares art this build cannot supply — a level that does not resolve, or one left
// out of the cook. UE greyboxes it, so the author gets a placeholder box where they expect a
// room, and nothing else in the UI would say why.
//
// Deliberately distinct from "no art yet" (`kind: 'greybox'`), which is an intentional state and
// not a problem to report. Only a build can tell these apart, so this returns false whenever the
// verdict is missing rather than guessing from the declared path.
export const isGeometryMisconfigured = (
  element: MapEditorCatalogElement,
): boolean => {
  const { geometry } = element;
  if (!geometry || geometry.kind === 'greybox') {
    return false;
  }

  return !geometry.available || geometry.cooked === false;
};
